import { useContext, useEffect, useState } from 'react';
import { FacebookShareButton, EmailShareButton, FacebookMessengerShareButton, WhatsappShareButton } from 'react-share';
import { Flex, Text, Box, Button, Image, Link, Menu, MenuButton, MenuList, MenuItem, useToast } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import AMMContext from 'context';
import useTranslation from 'next-translate/useTranslation';
import NextLink from 'next/link';
import { addToCart as apiAddToCart } from 'services/cart';
import { addToWishlist as apiAddToWishlist } from 'services/wishlist';
import useCart from 'hooks/useCart';
import { useRouter } from 'next/router';
import { FACEBOOK_APP_ID } from 'utils/config.js';
import { motion } from 'framer-motion';
import useWishlist from 'hooks/useWishlist';
import Tracking from 'utils/tracking';
import { getErrorMessage } from 'utils/helpers';
import Heart from 'components/icons/Heart';
import HeartFilled from 'components/icons/HeartFilled';
import { formatKlarnaPrice } from 'utils/helpers';
import useUser from 'hooks/useUser';

const MotionText = motion(Text);
export default function ProductInfo({
  title,
  body,
  variations,
  defaultVariation,
  relativeColours,
  masterSku,
  metalColor,
  additionalInformation,
  productData,
  isRing,
  weight,
  ringSizeGuide,
}) {
  const { t } = useTranslation('product');
  const toast = useToast();

  const router = useRouter();

  const [loadingAddToCart, setLoadingAddToCart] = useState(false);
  const [loadingAddToWishlist, setLoadingAddToWishlist] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(
    defaultVariation?.id ? variations.find((i) => i.id === defaultVariation?.id) : variations?.[0]
  );

  const { mutate } = useCart();
  const { mutate: wishListMutate, wishListData } = useWishlist();
  const { openCart } = useContext(AMMContext);
  const [lang, setLang] = useState('el-GR');
  const list = router.locale + router.asPath;
  const { userData } = useUser();

  useEffect(() => {
    router.locale === 'el' ? setLang('el-GR') : setLang('en-GR');
  }, [router.locale]);

  useEffect(() => {
    setSelectedVariation(defaultVariation?.id ? variations.find((i) => i.id === defaultVariation.id) : variations?.[0]);
  }, [defaultVariation?.id]);

  function createDescriptionMarkup() {
    return { __html: body };
  }

  function isInWishList() {
    return Boolean(wishListData.find((i) => i.variation_id === selectedVariation?.id));
  }

  const handleAddToCart = () => {
    setLoadingAddToCart(true);

    apiAddToCart(router.locale, [
      {
        purchased_entity_type: 'commerce_product_variation',
        purchased_entity_id: selectedVariation?.id,
        quantity: '1',
        combine: true,
      },
    ])
      .then(({ data }) => {
        mutate();

        Tracking.addToCart(data.order_items.slice(-1), data?.total_price, data, userData);
      })
      .catch((err) => {
        toast({
          // title: t('error'),
          description: getErrorMessage(err),
          position: 'top',
          // status: 'error',
          duration: 9000,
          isClosable: true,
        });
      })
      .finally(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          openCart();
          setLoadingAddToCart(false);
        }, 200);
      });
  };

  const handleAddToWishlist = () => {
    setLoadingAddToWishlist(true);
    apiAddToWishlist(router.locale, {
      product_variation: selectedVariation?.id,
    })
      .then(() => {
        wishListMutate();

        !isInWishList() && Tracking.addToWishlist(productData, true);
      })
      .catch((err) => {
        toast({
          description: getErrorMessage(err),
          position: 'top',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      })
      .finally(() => {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setLoadingAddToWishlist(false);
        }, 750);
      });
  };

  if (!masterSku) return null;

  return (
    <Flex flex={{ base: 1, lg: 4, xl: 3 }} direction={'column'} marginLeft={{ base: '0', lg: '35px' }} mt="15px">
      <Flex direction={'row'} justify={'space-between'} align={'center'} mb={'20px'}>
        <MotionText as={'h1'} fontWeight={400} fontSize={{ base: '26px', md: '36px' }} lineHeight={1.25}>
          {title}
        </MotionText>
        <Box className="sbl_i" display={'none'}>
          <span class="prod_id">{productData?.master_sku}</span>
          <span class="price_lang">{router.locale}</span>
        </Box>
      </Flex>
      <Box display="flex">
        {!productData?.last_price_change_date && defaultVariation?.list_price ? (
          <Box as={'p'} fontSize={'26px'} mb={'20px'} mr="12px" color={'lightGrey'}>
            <del>{defaultVariation?.list_price}</del>
          </Box>
        ) : (
          ''
        )}
        <Box as={'p'} fontWeight={700} fontSize={'26px'} mb={'10px'} color={'text.primary'} mr="12px">
          {defaultVariation?.price}
        </Box>

        {defaultVariation?.discount_percentage ? (
          <Box as={'p'} fontWeight={700} fontSize={'26px'} mb={'20px'} color={'brand.900'}>
            {defaultVariation?.discount_percentage}
          </Box>
        ) : (
          ''
        )}
      </Box>
      {/* {productData?.last_price_change_date && (
        <Box mb={'10px'} color={'gray'} fontSize={'16px'}>
          {t('common:lastPrice')}
          {`${defaultVariation?.list_price}`}
        </Box>
      )} */}

      {additionalInformation && (
        <Text
          as={'div'}
          fontWeight={600}
          fontSize={'14px'}
          mb={'20px'}
          dangerouslySetInnerHTML={{
            __html: additionalInformation,
          }}
        />
      )}

      <Text as={'p'} fontWeight={400} fontSize={'16px'} mb={'5px'}>
        <Text as={'span'}>
          <strong>{t('code')}:</strong>
        </Text>{' '}
        {masterSku}
      </Text>
      <Text as={'p'} fontWeight={400} fontSize={'16px'} mb={'5px'}>
        <Text as={'span'}>
          <strong>{t('color')}:</strong>
        </Text>{' '}
        {metalColor?.name}
      </Text>

      {productData?.size === 'none' || !productData?.size ? (
        ''
      ) : (
        <Box display={{ base: 'block', md: 'flex' }} justifyContent="space-between">
          <Text as={'p'} fontWeight={400} fontSize={'16px'} mb={{ base: '8px', sm: '5px' }}>
            <Text as={'span'}>
              <strong>{t('common:size')}:</strong>
            </Text>{' '}
            {productData?.size?.name}
          </Text>
          {isRing ? (
            <Box display="inline-flex" mb={{ base: '25px', md: '0px' }}>
              <NextLink href={ringSizeGuide} passHref prefetch={false}>
                <Link target="_blank">{t('mySize')}</Link>
              </NextLink>
              <Image w="22px" h="24px" ml="8px" src={'/assets/size-icon.png'} alt={'my size'} />
            </Box>
          ) : (
            ''
          )}
          {productData?.necklaceSizeguide ? (
            <Box display="inline-flex" mb={{ base: '25px', md: '0px' }}>
              <NextLink
                href={
                  router.locale === 'el'
                    ? 'https://www.annamariamazaraki.gr/el/size-guide'
                    : ' https://www.annamariamazaraki.gr/en/node/2322'
                }
                passHref
                prefetch={false}
              >
                <Link target="_blank">{t('mySize')}</Link>
              </NextLink>
              <Image w="22px" h="24px" ml="8px" src={'/assets/size-icon.png'} alt={'my size'} />
            </Box>
          ) : (
            ''
          )}
        </Box>
      )}
      {weight ? (
        <Text mb={{ base: '8px', sm: '5px' }}>
          <strong>{t('common:weight')}:</strong> {weight}
        </Text>
      ) : null}

      {relativeColours && relativeColours.length > 0 ? (
        <Box mb={'25px'} display="block">
          <Text as={'p'} fontWeight={400} fontSize={'16px'} mb={'15px'}>
            <strong>{t('chooseColor')}</strong>
          </Text>
          <Box>
            {relativeColours?.map(({ image, path, title }, index) => (
              <Box as={'div'} marginRight="8px" w="20%" display="inline-block" key={`relativeColours-${index}`}>
                <NextLink href={path} passHref prefetch={false}>
                  <Link>
                    <Image src={image} paddingRight="8px" alt={title} />
                  </Link>
                </NextLink>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box mb="25px" />
      )}

      {variations && variations.length > 1 ? (
        <Box mb={'20px'} display="flex" alignItems="center" justifyContent="space-between">
          <Menu>
            <MenuButton as={Button} w="180px" variant={'outline'} borderColor="#CACACA" rightIcon={<ChevronDownIcon />}>
              {selectedVariation ? selectedVariation?.name : t('chooseSize')}
            </MenuButton>
            <MenuList>
              {variations?.map((i) => (
                <MenuItem
                  key={`variation-${i.id}`}
                  onClick={() => {
                    setSelectedVariation(i);
                  }}
                >
                  {i.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Box>
      ) : null}

      <Box display="flex" alignItems={'center'} mb={'20px'}>
        <Button
          w={'100%'}
          onClick={handleAddToCart}
          isLoading={loadingAddToCart}
          isDisabled={selectedVariation?.stock_level > 0 && selectedVariation?.status ? false : true}
        >
          {selectedVariation?.stock_level > 0 && selectedVariation?.status ? t('common:addToCard') : t('outOfStock')}
        </Button>
        <Flex justifyContent={'space-between'} mt={'5px'} mb={'20px'} />

        <Box
          opacity={loadingAddToWishlist ? 0.5 : 1}
          cursor="pointer"
          ml="10px"
          onClick={handleAddToWishlist}
          isLoading={loadingAddToWishlist}
        >
          {isInWishList() ? <HeartFilled boxSize={'29px'} /> : <Heart boxSize={'29px'} color={'#D6006D'} />}
        </Box>
      </Box>
      <klarna-placement
        key={lang}
        data-key="credit-promotion-badge"
        data-locale={lang}
        data-purchase-amount={formatKlarnaPrice(defaultVariation?.price)}
      ></klarna-placement>

      <Box mt={'20px'} mb={'20px'}>
        <Text as={'p'} fontWeight={700} fontSize={'16px'}>
          {t('productInfo')}
        </Text>
        <Text as={'div'} dangerouslySetInnerHTML={createDescriptionMarkup()} />
      </Box>

      <Box mb={'20px'}>
        <Text as={'p'} fontSize={'16px'} mb={'20px'} display="flex" alignItems="center">
          <Image src={'/assets/icon.svg'} display="inline-block" paddingRight="12px" alt="contact us" />
          <NextLink href={'tel:210 6848300'} passHref prefetch={false}>
            <Link marginTop="-4px">
              <Image
                src={'/assets/phone-icon-black.svg'}
                display="inline-block"
                paddingRight="12px"
                w="30px"
                alt="phone: 210 6848300"
              />
            </Link>
          </NextLink>
          <NextLink href={'tel:210 6848300'} passHref prefetch={false}>
            <Link>210 6848300</Link>
          </NextLink>
        </Text>
      </Box>

      <Box mb={'20px'}>
        <Text as={'div'} fontWeight={700} fontSize={'16px'} mb={'20px'} display="flex">
          {t('share')}
          <Box as={'ul'} listStyleType="none" display="flex" marginLeft="10px">
            <Box as={'li'}>
              <EmailShareButton subject={title} style={{ outline: 'none' }}>
                <Image
                  src={'/assets/emailSocialProductInfo.svg'}
                  paddingRight="8px"
                  outline="none"
                  alt="share email:"
                />
              </EmailShareButton>
            </Box>
            <Box as={'li'}>
              <FacebookShareButton quote={title} url="www.facebook.com" style={{ outline: 'none' }}>
                <Image src={'/assets/fbSocialProductInfo.svg'} paddingRight="8px" alt="share facebook:" />
              </FacebookShareButton>
            </Box>
            <Box as={'li'}>
              <WhatsappShareButton url="www.whatsapp.com" title={title} style={{ outline: 'none' }}>
                <Image src={'/assets/viberSocialProductInfo.svg'} paddingRight="8px" alt="share whatsapp:" />
              </WhatsappShareButton>
            </Box>
            <Box as={'li'} outline="none">
              <FacebookMessengerShareButton appId={FACEBOOK_APP_ID} url="www.messenger.com" style={{ outline: 'none' }}>
                <Image src={'/assets/messengersocialProductInfo.svg'} paddingRight="8px" alt="share messenger:" />
              </FacebookMessengerShareButton>
            </Box>
          </Box>
        </Text>
      </Box>
    </Flex>
  );
}
